import { t } from "@lingui/macro";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import {
  CheckCircle2,
  Info,
  Target,
  AlertTriangle,
  Lightbulb,
  Award,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";

type AchievementRelevanceIndexProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const AchievementRelevanceIndex = ({
  analysesData,
  className,
}: AchievementRelevanceIndexProps) => {
  const { isDarkMode } = useTheme();
  const [showAllGoals, setShowAllGoals] = useState(false);
  const [showAllGaps, setShowAllGaps] = useState(false);
  const [showAllAchievements, setShowAllAchievements] = useState(false);

  // Initial number of items to show
  const INITIAL_ITEMS_TO_SHOW = 4;

  // Return null if data doesn't exist
  if (!analysesData.metrics.ari || !analysesData.metrics.ari.object) {
    return null;
  }

  const { job_goals, resume_achievements, gap_analysis, implicit_skills } =
    analysesData.metrics.ari.object;

  // Get overall score from analysis results - same calculation as metric-score-card.tsx
  const score =
    analysesData.metrics.ari.weight > 0
      ? analysesData.metrics.ari.weightedScore / analysesData.metrics.ari.weight
      : 0;

  // Define colors - unified with metric-score-card.tsx
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    amber: "#ffb74d",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on score - unified with metric-score-card.tsx
  const achievementPercentage = Math.round(score * 100);
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (achievementPercentage >= 80) status = "excellent";
  else if (achievementPercentage >= 60) status = "good";

  // Get status text and colors based on status - unified with metric-score-card.tsx
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`This aspect of your resume is working very well for this job.`,
          action: t`Keep doing what you're doing - this is a strength!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`This area is decent but could be improved for better results.`,
          action: t`Review the suggestions below to make this even stronger.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`This area needs significant improvement to match the job requirements.`,
          action: t`Focus on the recommendations below to improve your chances.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Filter gaps to show only the most important ones (not found and partially found)
  const criticalGaps = gap_analysis
    .filter((gap) => gap.status === "not_found" || gap.status === "partially_found")
    .slice(0, 5); // Show max 5 critical gaps

  // Get top achievements (high weight only)
  const topAchievements = resume_achievements
    .filter((achievement) => achievement.weight.overall === "high")
    .slice(0, 6); // Show max 6 top achievements

  // Get high-confidence implicit skills
  const relevantImplicitSkills = implicit_skills
    .filter((skill) => skill.confidence === "high")
    .slice(0, 4); // Show max 4 most relevant

  // Prepare data for display
  const goalsToShow = showAllGoals ? job_goals : job_goals.slice(0, INITIAL_ITEMS_TO_SHOW);
  const gapsToShow = showAllGaps ? criticalGaps : criticalGaps.slice(0, 3);
  const achievementsToShow = showAllAchievements ? topAchievements : topAchievements.slice(0, 3);

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Info className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {statusInfo.text} ({achievementPercentage}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Critical Gaps - What's missing */}
        {criticalGaps.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                {t`Areas to Strengthen`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Requirements that are missing or weak in your resume. Address these to improve your chances.`}
              </p>
            </div>
            <div className="space-y-3">
              {gapsToShow.map((gap, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-800/50"
                >
                  <div className="flex items-start gap-x-3">
                    <div className="flex-1">
                      <p className="mb-1 text-sm font-medium">{gap.requirement}</p>
                      <p className="text-sm">
                        <strong>{t`Suggestion:`}</strong> {gap.suggestion}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {criticalGaps.length > 3 && (
                <button
                  onClick={() => setShowAllGaps(!showAllGaps)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllGaps ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${criticalGaps.length - 3} More Areas`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Job Goals - What the employer wants */}
        {job_goals.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Target className="h-5 w-5 text-blue-600" />
                {t`What This Job Requires`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Key goals and responsibilities for this position. Make sure your resume demonstrates experience with these areas.`}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {goalsToShow.map((goal, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50"
                >
                  <div className="flex items-start gap-x-3">
                    <div
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                      style={{ backgroundColor: colors.blueBg, color: colors.blue }}
                    >
                      {index + 1}
                    </div>
                    <p className="text-sm leading-relaxed">{goal}</p>
                  </div>
                </div>
              ))}

              {job_goals.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllGoals(!showAllGoals)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllGoals ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${job_goals.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Hidden Strengths - Implicit skills */}
        {relevantImplicitSkills.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Lightbulb className="h-5 w-5 text-green-600" />
                {t`Hidden Strengths`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Skills you have that aren't explicitly mentioned in your resume. Consider adding these to make your experience more visible.`}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {relevantImplicitSkills.map((skill, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20"
                >
                  <p className="mb-1 text-sm font-medium text-green-800 dark:text-green-200">
                    {skill.skill}
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    {t`Based on:`} {skill.evidence_context}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Achievements - What you've accomplished */}
        {topAchievements.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Award className="h-5 w-5 text-purple-600" />
                {t`Your Strongest Achievements`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Your most impactful accomplishments that align with this job. Highlight these in your application.`}
              </p>
            </div>
            <div className="space-y-3">
              {achievementsToShow.map((achievement, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50"
                >
                  <p className="mb-2 text-sm font-medium">{achievement.achievement}</p>

                  <div className="mb-2 flex items-center gap-x-2 text-xs text-slate-600 dark:text-slate-400">
                    <span>{achievement.context}</span>
                  </div>

                  {/* Show metrics if available */}
                  {achievement.metrics.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {achievement.metrics.slice(0, 3).map((metric, metricIndex) => (
                        <div
                          key={metricIndex}
                          className="flex items-center gap-x-1 rounded-full px-2 py-1 text-xs"
                          style={{ backgroundColor: colors.greenBg, color: colors.green }}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          <span>{metric}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {topAchievements.length > 3 && (
                <button
                  onClick={() => setShowAllAchievements(!showAllAchievements)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllAchievements ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${topAchievements.length - 3} More Achievements`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
